import { useCallback, useEffect, useState, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
    Dialog, DialogTitle, DialogContent, Typography, List, ListItem,
    ListItemText, Button, IconButton, Box, Checkbox, ListItemIcon,
    TextField, InputAdornment, Skeleton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Loader from '../../common/Loader';
import { useSelector } from 'react-redux';
import { Search, Work } from '@mui/icons-material';
import { Postrequestcall } from '../../request/Postrequest';
import { FETCH_CLIENT_JOBS } from '../../constant/Apipath';
import { ClientEnglishFallback } from '../constant/ClientEnglishFallback';
import SearchOffIcon from '@mui/icons-material/SearchOff';

export default function JobList({ open, onClose, onSelect, selectedJobs = [] }) {
    const { loginData, selectedClientData, multiLanguageData } = useSelector((state) => state?.main);
    const [localSelected, setLocalSelected] = useState(selectedJobs);
    const [isJobListLoading, setIsJobListLoading] = useState(false);
    const [jobData, setJobData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const pageSize = 20;
    const totalCountRef = useRef(null);

    const accentColor = selectedClientData?.AccentColor || '#4F6EF7';

    const LANGUAGE = ClientEnglishFallback.ClientInspections(
        multiLanguageData?.ClientInspections || {}
    );
    const { TEXT_SELECT_JOBS, TEXT_SEARCH_FOR_A_JOB, TEXT_NO_JOBS_FOUND, TEXT_SELECT } = LANGUAGE;

    const getClientJob = useCallback(
        async (query = '', pageNr = 1, append = false) => {
            if (pageNr === 1) setIsJobListLoading(true);
            try {
                const obj = {
                    PageNr: pageNr,
                    NrOfRecPerPage: pageSize,
                    FullSearch: query || '',
                    UserId: loginData?.userInformation?.UserId,
                    IncludeRecordNr: true,
                    FetchAllowedRecordsOnly: true,
                    SearchList: [{ ClientId: selectedClientData?.ClientId, active: true }],
                    SortList: [{ FieldName: 'dateCreated', Direction: 'DESC' }],
                };
                const clientJobResponse = await Postrequestcall(
                    FETCH_CLIENT_JOBS,
                    obj,
                    loginData?.accessToken
                );
                if (clientJobResponse?.status === 200) {
                    const datas = clientJobResponse?.data?.data || [];
                    const updatedData = datas.map((data) => ({
                        id: data.id,
                        label: data.name,
                        fullLabel: data.jobCode + ' - ' + data.name,
                    }));
                    if (append) {
                        setJobData((prev) => [...prev, ...updatedData]);
                    } else {
                        setJobData(updatedData);
                    }
                    if (typeof clientJobResponse?.data?.nrOfRecords === 'number') {
                        totalCountRef.current = clientJobResponse.data.nrOfRecords;
                        setHasMore(pageNr * pageSize < clientJobResponse.data.nrOfRecords);
                    } else {
                        setHasMore(updatedData.length === pageSize);
                    }
                } else {
                    setHasMore(false);
                }
            } catch (error) {
                setHasMore(false);
                console.log('getClientJob-> Error: ', error);
            } finally {
                if (pageNr === 1) setIsJobListLoading(false);
            }
        },
        [loginData, selectedClientData]
    );

    const handleToggle = (job) => {
        if (localSelected.some((j) => j.id === job.id)) {
            setLocalSelected(localSelected.filter((j) => j.id !== job.id));
        } else {
            setLocalSelected([...localSelected, job]);
        }
    };

    const handleSubmit = () => {
        onSelect(localSelected);
        onClose();
    };

    useEffect(() => {
        setPage(1);
        getClientJob(searchQuery, 1, false);
    }, [getClientJob, searchQuery]);

    const fetchMoreData = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        getClientJob(searchQuery, nextPage, true);
    };

    const selectedCount = localSelected?.length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    height: '560px',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.14)',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    px: 3,
                    pt: 3,
                    pb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    borderBottom: '1px solid #F0F2F5',
                }}
            >
                <Box>
                    <Typography sx={{ fontWeight: 700, color: '#1A1D23', letterSpacing: '-0.02em', fontSize: '1.05rem' }} variant="h6">
                        {TEXT_SELECT_JOBS}
                    </Typography>
                    {selectedCount > 0 && (
                        <Typography variant="body2" sx={{ color: accentColor, mt: 0.3, fontSize: '0.78rem', fontWeight: 600 }}>
                            {selectedCount} selected
                        </Typography>
                    )}
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        bgcolor: '#F5F6FA',
                        color: '#6B7280',
                        borderRadius: '10px',
                        '&:hover': { bgcolor: '#ECEEF2', color: '#1A1D23' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* Content */}
            <DialogContent
                dividers
                sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    overflow: 'hidden',
                    borderTop: 'none',
                    borderBottom: 'none',
                }}
            >
                {/* Search bar */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 1.8,
                        bgcolor: '#FAFBFC',
                        borderBottom: '1px solid #F0F2F5',
                        flexShrink: 0,
                    }}
                >
                    <TextField
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        fullWidth
                        placeholder={TEXT_SEARCH_FOR_A_JOB}
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                bgcolor: '#fff',
                                fontSize: '0.875rem',
                                '& fieldset': { borderColor: '#E5E7EB' },
                                '&:hover fieldset': { borderColor: '#D1D5DB' },
                                '&.Mui-focused fieldset': { borderColor: accentColor, borderWidth: 1.5 },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 18, color: '#9499A8' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {/* List */}
                <Box
                    sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}
                    id="job-list-scrollable-div"
                >
                    {isJobListLoading && page === 1 ? (
                        <List disablePadding>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        mb: 1.2,
                                        p: 1.5,
                                        border: '1px solid #ECEEF2',
                                        borderRadius: '12px',
                                    }}
                                >
                                    <Skeleton variant="rounded" width={20} height={20} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton variant="text" width="75%" height={18} />
                                    </Box>
                                </Box>
                            ))}
                        </List>
                    ) : jobData?.length > 0 ? (
                        <InfiniteScroll
                            dataLength={jobData.length}
                            next={fetchMoreData}
                            hasMore={hasMore}
                            loader={
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <Loader />
                                </Box>
                            }
                            scrollableTarget="job-list-scrollable-div"
                            style={{ overflow: 'visible' }}
                        >
                            <List disablePadding sx={{ width: '100%' }}>
                                {jobData.map((job) => {
                                    const isSelected = localSelected.some((j) => j.id === job.id);
                                    return (
                                        <ListItem
                                            key={job.id}
                                            onClick={() => handleToggle(job)}
                                            sx={{
                                                border: isSelected
                                                    ? `1.5px solid ${accentColor}55`
                                                    : '1px solid #ECEEF2',
                                                borderRadius: '6px',
                                                mb: 1,
                                                cursor: 'pointer',
                                                bgcolor: isSelected ? `${accentColor}08` : '#fff',
                                                transition: 'all 0.15s ease',
                                                px: 1.5,
                                                py: 0.8,
                                                '&:hover': {
                                                    bgcolor: isSelected ? `${accentColor}12` : '#FAFBFC',
                                                    borderColor: isSelected ? `${accentColor}77` : '#D1D5DB',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Checkbox
                                                    edge="start"
                                                    checked={isSelected}
                                                    disableRipple
                                                    size="small"
                                                    sx={{
                                                        p: 0,
                                                        color: '#D1D5DB',
                                                        marginLeft:'2px',
                                                        '&.Mui-checked': { color: accentColor },
                                                        '& .MuiSvgIcon-root': { fontSize: 20 },
                                                    }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={job.fullLabel}
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: isSelected ? 600 : 400,
                                                    color: isSelected ? '#1A1D23' : '#374151',
                                                }}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </InfiniteScroll>
                    ) : (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 6,
                                gap: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: '14px',
                                    bgcolor: '#F5F6FA',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <SearchOffIcon sx={{ fontSize: 26, color: '#BCC0CC' }} />
                            </Box>
                            <Typography variant="body2"  sx={{color: "#9499A8",  fontWeight: 500 }}>
                                {TEXT_NO_JOBS_FOUND}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* Footer */}
            <Box
                sx={{
                    px: 2.5,
                    py: 2,
                    borderTop: '1px solid #F0F2F5',
                    bgcolor: '#FAFBFC',
                    flexShrink: 0,
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    disabled={selectedCount === 0}
                    onClick={handleSubmit}
                    sx={{
                        bgcolor: selectedCount === 0 ? '#E5E7EB' : accentColor,
                        color: selectedCount === 0 ? '#9CA3AF' : 'white',
                        borderRadius: '12px',
                        py: 1.3,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        boxShadow: selectedCount > 0 ? `0 4px 14px ${accentColor}44` : 'none',
                        '&:hover': {
                            bgcolor: selectedCount === 0 ? '#E5E7EB' : accentColor,
                            opacity: selectedCount > 0 ? 0.92 : 1,
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    {TEXT_SELECT}
                    {selectedCount > 0 && (
                        <Box
                            component="span"
                            sx={{
                                ml: 1,
                                bgcolor: 'rgba(255,255,255,0.25)',
                                borderRadius: '6px',
                                px: 0.8,
                                py: 0.15,
                                fontSize: '0.8rem',
                            }}
                        >
                            {selectedCount}
                        </Box>
                    )}
                </Button>
            </Box>
        </Dialog>
    );
}